<template>
   <li
      :key="`doc-${doc.id}`"
      class="block items-center border-t border-grey-20 py-6 text-base first:border-t-0 md:flex md:border-t-0 md:py-3"
      :data-cy="`doc-${doc.id}`"
   >
      <p class="order-1 mb-4 ml-3 flex shrink-0 items-center px-1 md:mb-0 md:ml-0 md:w-1/3 md:justify-start lg:px-2">
         <svg
            class="absolute -left-3.5 mr-2 shrink-0 md:hidden"
            width="11"
            height="14"
            aria-hidden="true"
            focusable="false"
         >
            <use xlink:href="#doc" />
         </svg>
         <span data-cy="doc-title" class="break-all pr-0 md:pr-4">{{ doc.title }}</span>
      </p>
      <!-- This might be a new design for the pillTags
      <p class="order-3 mb-4 block px-1 md:my-0 md:ml-0 md:w-1/3 lg:px-2">
         <span class="sr-only">. {{ $t("account.docs.type") }}: </span>
         <span>{{ doc.docTypeLabel }}</span>
      </p> -->
      <p class="order-3 mb-4 ml-3 mt-1 block px-1 md:my-0 md:ml-0 md:w-1/3 lg:px-2">
         <span class="sr-only">. {{ $t("account.docs.type") }}: </span>
         <span
            class="text-bolder mr-2 rounded-full px-2 py-1 uppercase"
            style="font-size: 0.75rem"
            data-cy="doc-type-pill"
            :class="[
               { 'bg-purple-10': doc.docTypePillColor === 'purple' },
               { 'bg-sand-10': doc.docTypePillColor === 'sand' },
            ]"
         >
            <span>{{ doc.docTypeLabel }}</span>
         </span>
         <span v-if="!!doc.lang" class="sr-only">. {{ $t("account.docs.language") }}: </span>
         <span
            v-if="!!doc.lang"
            data-cy="doc-lang-pill"
            class="text-bolder rounded-full bg-grey-10 px-2 py-1 uppercase"
            style="font-size: 0.75rem"
         >
            <span>{{ doc.lang }}</span
            ><span class="sr-only">.</span>
         </span>
      </p>
      <p class="order-2 mb-4 ml-3 block px-1 md:my-0 md:ml-0 md:w-1/6 lg:px-2">
         <span class="md:sr-only">{{ $t("account.docs.date") }}: </span>
         <time data-cy="doc-date" :datetime="doc.docDate">{{ doc.formattedDate }}</time>
      </p>
      <button
         data-cy="show-doc-btn"
         :aria-label="`${$t('account.docs.view')}: ${doc.title}`"
         class="order-4 mb-4 ml-3 hidden px-1 md:my-0 md:ml-0 md:block md:w-1/6 lg:px-2"
         @click="checkShowDocument(doc.docType, doc.id, doc.downloadTitle)"
      >
         <span class="underline">{{ $t("account.docs.view") }}</span>
      </button>
      <!-- Show a download CTA directly on smaller screens as most mobile browsers don't support inline pdfs -->
      <button
         data-cy="download-doc-btn"
         :aria-label="`${$t('account.docs.download')}: ${doc.title}`"
         class="order-4 ml-3 block px-1 md:my-0 md:ml-0 md:hidden md:w-1/6 lg:px-2"
         @click="checkDownloadDocument(doc.docType, doc.id, doc.downloadTitle)"
      >
         <span class="underline">{{ $t("account.docs.download") }}</span>
      </button>
   </li>
</template>

<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";
import { useToastKey } from "@/plugins/useToast";
import DocumentsApi from "@/data/api-layer/documentsApi";
import { useFormattedDate } from "@/composables/useFormattedDate";
import type { DocumentResp } from "@/data/api-layer/documentsApi";
import { studyReportTypes } from "@/constants/study-reports.ts";
import { toCamelCase } from "@/js/helpers/camelCase";

const { localePath } = useRouteLocale();
const useToast = inject(useToastKey);
const i18n = useI18n();
const $t = useI18n().t;

interface State {
   documentDownloadUrls: string | null;
   documentDownloadLinks: HTMLAnchorElement | null;
}

interface Doc {
   id: string;
   title: string;
   downloadTitle: string;
   docType: string;
   docTypeLabel: string;
   docTypePillColor: string | null;
   docDate: string;
   formattedDate: string;
   lang: string;
}

interface Props {
   document: DocumentResp;
}

const state = reactive<State>({
   documentDownloadLinks: null,
   documentDownloadUrls: null,
});

const props = defineProps<Props>();

const doc = computed((): Doc => {
   let studyDoc;

   // try & catch block - we want to throw an error if expected information is missing from the data coming from the BE
   try {
      // For each doc, get the correct translation of the doc type label
      let docTypeLabel = $t("account.docs.types.other");
      let docTypePillColor: string | null = null;

      // study report of any kind
      if (studyReportTypes.includes(props.document.type?.name)) {
         docTypeLabel = $t("account.docs.types.report");
         docTypePillColor = "purple";
      }
      // flow consent generated after completing the consent block
      else if (props.document.type?.name === "survey-consent") {
         docTypeLabel = $t("account.docs.types.consent");
         docTypePillColor = "sand";
      }

      // Fallback to current site language, but in future we should have a 'language' property
      // (4 letter language code) sent through as part of the information for each doc
      let lang = i18n.locale.value ? i18n.locale.value.substring(0, 2) : "";

      if (props.document.localeId) lang = props.document.localeId.substring(0, 2);

      // Format date and titles
      const formattedDate = useFormattedDate(props.document.createdAt, "short");
      let filenameTitle = `${docTypeLabel.replace(/\s/g, "_")}_${lang}`;

      if (studyReportTypes.includes(props.document.type?.name)) {
         // Fix: handle case where props.document.name might be null
         const documentName = props.document.name || "";
         filenameTitle = `Study_report_${documentName ? `${toCamelCase(documentName)}_` : ""}${props.document.id}`;
      }

      const associatedStudyTitle = props.document.study?.title || "Document";
      const filenameStudyTitle = associatedStudyTitle.replace(/[\s()\-\/*+%]/g, "_");

      studyDoc = {
         id: props.document.id,
         title: `${filenameTitle}.pdf`,
         downloadTitle: `${filenameStudyTitle}_${filenameTitle}.pdf`,
         docType: props.document.type?.name,
         docTypeLabel,
         docTypePillColor,
         docDate: props.document.createdAt,
         formattedDate,
         lang,
      };
   } catch (e) {
      console.error("Error fetching documents data", e);
      // Show a popup to explain the problem so as to avoid silent fails
      useToast({ title: $t("validation.docs_fetch_error"), text: $t("validation.try_again"), type: "error" });
   }
   return studyDoc;
});

const getGeneticReportReminderToastOptions = (confirmFunction, docId, docTitle) => {
   useToast({
      title: $t("account.docs.view_q"),
      position: "center",
      width: 592,
      padding: "1.25rem 0.5rem 2.5rem 0.5rem",
      html: `
         <div class="text-center max-w-sm mx-auto" data-cy="modal-links-to-${docId}">
            <p class="text-lighter mb-6 leading-tight">${$t("account.docs.gentle_reminder")}</p>
            <p class="text-lighter leading-tight">${$t("account.docs.you_can_return")}</p>
         </div>
      `,
      confirmButtonText: $t("system.button_continue"),
      showCancelButton: true,
   }).then((result) => {
      if (result.isConfirmed) confirmFunction(docId, docTitle);
   });
};

const checkShowDocument = async (docType, docId, docTitle) => {
   // Show a gentle reminder message before opening a document with genetic report info
   if (studyReportTypes.includes(docType)) getGeneticReportReminderToastOptions(showDocument, docId, docTitle);
   else showDocument(docId);
};

const showDocument = (docId) => {
   // If the user clicks continue, or the doctype is not genetic report, open the doc in a new tab
   window.open(`${localePath.value}/document/${docId}`, "_blank");
};

const checkDownloadDocument = async (docType, docId, docTitle) => {
   // Show a gentle reminder message before downloading a document with genetic report info
   if (studyReportTypes.includes(docType)) getGeneticReportReminderToastOptions(downloadDocument, docId, docTitle);
   else downloadDocument(docId, docTitle);
};

const downloadDocument = async (docId, docTitle) => {
   // If the user clicks continue, or the doctype is not genetic report, download the doc
   // Unless an objectURL has already been created for the pdf doc, try to do this
   if (!state.documentDownloadLinks) {
      try {
         const pdfResponse = await DocumentsApi.getDocument(docId);
         // https://developer.mozilla.org/en-US/docs/Web/API/Blob/Blob
         const docDataBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
         const blobUrl = URL.createObjectURL(docDataBlob);

         // Save the object url so that we don't repeat this process unecessarily, and can remove it on unmount
         state.documentDownloadUrls = blobUrl;
         // Create a download link and click it to download the document
         const downloadLink = document.createElement("a");
         downloadLink.style.display = "none";
         downloadLink.href = blobUrl;
         downloadLink.download = docTitle;
         document.body.appendChild(downloadLink);
         // Also save the reference to this link. In the (unlikely) scenario that someone clicks to download multiple times we don't need to add multiple links to the page
         state.documentDownloadLinks = downloadLink;
         downloadLink.click();
      } catch (error) {
         console.error("Error fetching data", error);
         // We'll get a 404 error if we try to access a doc that doesn't exist or that we don't have permission to view
         // Show a popup to explain the problem
         useToast({ title: $t("validation.pdf_fetch_error"), type: "error" });
      }
   } else {
      state.documentDownloadLinks.click();
   }
};
</script>
